/* Batch 10.1 · R2 — "Roll forward to next year" dialog (owner OK 10-Oct-2026).
   The new engagement's name and year (FY+1, period + 1 year), what is carried / not carried, and the documents ticked
   "Permanent file" (each ticked here by default; only these can be carried). Documents that LOOK like permanent-file
   documents but are not ticked are listed as a suggestion — they are never ticked automatically. The roll forward runs as a
   background job; the dialog shows its progress line, then offers to open the new engagement. Partner / Manager only. */
import React, { useEffect, useRef, useState } from "react";
import api, { toErrMsg } from "@/lib/api";
import { useI18n } from "@/contexts/I18nContext";
import { fmtDate } from "@/lib/dt";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import DateInput from "@/components/DateInput";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { capJobText } from "@/lib/engagementCap";   // Pre-Step 4 · 4b: a refused job shows the cap text (EN / AR)
import { asArray } from "@/lib/asArray";

export default function RollForwardDialog({ eng, open, onOpenChange, onDone }) {
  const { t, lang } = useI18n();
  const [pv, setPv] = useState(null);
  const [name, setName] = useState("");
  const [picked, setPicked] = useState({});
  const [job, setJob] = useState(null);
  const [busy, setBusy] = useState(false);
  const [dates, setDates] = useState({ start: "", end: "" });     // Batch 11 · B1: asked only when the source has none
  const timer = useRef(null);

  useEffect(() => {
    if (!(open && eng)) return undefined;
    setPv(null); setJob(null); setBusy(false); setDates({ start: "", end: "" });
    api.get(`/engagements/${eng.id}/roll-forward/preview`).then((r) => {
      setPv(r.data);
      setName(r.data.default_name || "");
      setPicked(Object.fromEntries(asArray(r.data.permanent_documents).map((d) => [d.id, true])));
    }).catch((e) => toast.error(toErrMsg(e, t("engagements.rfFailed"))));
    return () => { if (timer.current) clearTimeout(timer.current); };
  }, [open, eng, t]);

  const poll = (id) => {
    timer.current = setTimeout(async () => {
      try {
        const r = await api.get(`/jobs/${id}`, { _silent: true });
        setJob(r.data);
        if (r.data.status === "done") { setBusy(false); toast.success(t("engagements.rfDone").replace("{name}", r.data.result?.name || name)); onDone && onDone(r.data.result || {}); return; }
        if (r.data.status === "failed") { setBusy(false); toast.error(capJobText(r.data, lang) || t("engagements.rfFailed")); return; }   // Pre-Step 4 · 4b
      } catch (_) { /* keep polling */ }
      poll(id);
    }, 1200);
  };

  const start = async () => {
    setBusy(true);
    try {
      const ids = Object.entries(picked).filter(([, v]) => v).map(([k]) => k);
      const r = await api.post(`/engagements/${eng.id}/roll-forward`, { name, document_ids: ids,
        ...(pv?.dates_required ? { period_start: dates.start, period_end: dates.end } : {}) });
      setJob({ id: r.data.job_id, status: "queued", progress_pct: 0, message: t("engagements.rfQueued") });
      poll(r.data.job_id);
    } catch (e) {
      setBusy(false);
      toast.error(toErrMsg(e, t("engagements.rfFailed")));
    }
  };

  const pct = Math.max(0, Math.min(100, Number(job?.progress_pct || 0)));
  const done = job?.status === "done";
  const docName = (d) => d.original_filename || d.doc_ref || d.id;
  const already = pv?.already_rolled_forward_to;
  return (
    <Dialog open={open} onOpenChange={(v) => { if (!busy) onOpenChange(v); }}>
      <DialogContent className="max-w-xl" data-testid="eng-rf-dialog">
        <DialogHeader>
          <DialogTitle>{t("engagements.rfTitle")}</DialogTitle>
          <DialogDescription>{t("engagements.rfDesc")}</DialogDescription>
        </DialogHeader>
        {!pv ? <div className="text-sm text-muted-foreground">{t("common.loading")}</div> : (
          <div className="space-y-3 text-sm">
            {already && (
              <div className="rounded border border-amber-300 bg-amber-50 p-2 text-xs text-amber-900 dark:bg-amber-950/30 dark:text-amber-200" data-testid="eng-rf-already">
                {t("engagements.rfAlready").replace("{fy}", String(already.financial_year || ""))}
              </div>
            )}
            <div>
              <Label htmlFor="eng-rf-name">{t("engagements.rfName")}</Label>
              <Input id="eng-rf-name" value={name} onChange={(e) => setName(e.target.value)} disabled={busy || done} data-testid="eng-rf-name" />
            </div>
            {pv.dates_required ? (
              <div className="space-y-1" data-testid="eng-rf-dates">
                <div className="text-xs text-amber-800">{t("engagements.rfDatesAsk").replace("{fy}", String(pv.financial_year || ""))}</div>
                <div className="flex gap-2">
                  <label className="text-xs">{t("engagements.rfStartLabel")}
                    <DateInput value={dates.start} onChange={(v) => setDates({ ...dates, start: v })} data-testid="eng-rf-start-date" />
                  </label>
                  <label className="text-xs">{t("engagements.rfEndLabel")}
                    <DateInput value={dates.end} onChange={(v) => setDates({ ...dates, end: v })} data-testid="eng-rf-end-date" />
                  </label>
                </div>
              </div>
            ) : (
              <div className="text-xs" data-testid="eng-rf-year">
                {t("engagements.rfYear").replace("{fy}", String(pv.financial_year || ""))
                  .replace("{from}", fmtDate(pv.period_start, lang) || "—").replace("{to}", fmtDate(pv.period_end, lang) || "—")}
              </div>
            )}
            <div className="rounded border bg-slate-50 p-2 text-xs text-muted-foreground dark:bg-slate-900" data-testid="eng-rf-rules">
              <div>{t("engagements.rfCarried")}</div>
              <div className="mt-1">{t("engagements.rfNotCarried")}</div>
            </div>
            <div>
              <div className="mb-1 font-medium">{t("engagements.rfDocs")}</div>
              {(pv.permanent_documents || []).length === 0 ? (
                <div className="text-xs text-muted-foreground" data-testid="eng-rf-docs-none">{t("engagements.rfDocsNone")}</div>
              ) : asArray(pv.permanent_documents).map((d) => (
                <label key={d.id} className="flex items-center gap-2 text-xs">
                  <Checkbox checked={!!picked[d.id]} disabled={busy || done}
                            onCheckedChange={(v) => setPicked({ ...picked, [d.id]: !!v })} data-testid={`eng-rf-doc-${d.id}`} />
                  <span>{d.doc_ref ? `${d.doc_ref} · ` : ""}{docName(d)}</span>
                </label>
              ))}
              {(pv.suggested_documents || []).length > 0 && (
                <div className="mt-2 text-[11px] text-muted-foreground" data-testid="eng-rf-suggested">
                  {t("engagements.rfSuggested")} {asArray(pv.suggested_documents).slice(0, 8).map(docName).join(" · ")}
                </div>
              )}
            </div>
            {job && (
              <div data-testid="eng-rf-progress">
                <div className="h-2 w-full rounded bg-slate-200"><div className={`h-2 rounded ${job.status === "failed" ? "bg-red-600" : "bg-emerald-600"}`} style={{ width: `${done ? 100 : pct}%` }} /></div>
                <div className="mt-1 text-xs text-muted-foreground" data-testid="eng-rf-line">
                  {done ? t("engagements.rfDone").replace("{name}", job.result?.name || name)
                    : (job.status === "failed" ? t("engagements.rfFailed") : (job.message || t("engagements.rfQueued")))} {!done && job.status !== "failed" ? `· ${pct}%` : ""}
                </div>
              </div>
            )}
          </div>
        )}
        <DialogFooter>
          {!done && <Button variant="outline" disabled={busy} onClick={() => onOpenChange(false)}>{t("common.cancel")}</Button>}
          {!done && <Button disabled={busy || !pv || !!already || !name.trim() || (pv?.dates_required && !(dates.start && dates.end))} onClick={start} data-testid="eng-rf-start">{busy ? t("engagements.rfRunning") : t("engagements.rfStart")}</Button>}
          {done && <Button onClick={() => onOpenChange(false, job.result)} data-testid="eng-rf-open">{t("engagements.rfOpen")}</Button>}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
